import { Suspense, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';

import getUserFromAccessToken from '@preqin/preqin-core/cjs/OIDC/helpers/user';
import { useOidcAccessToken } from '@preqin/preqin-core/cjs/OIDC/oidc.utils';

import { User } from '@/types';

import { useGetWorkspaceNameReports } from './api/ReportsApi';
import { AppLoader } from './components/Loader/AppLoader';
import { findWorkspaceByName, isIntercomEnabled } from './constants/workspaceNames';
import { PowerBIProvider } from './context/PowerBIContext';
import ApplicationLayout from './layout/ApplicationLayout';
import { timeToExpire } from './utils/timeToExpire';
import Routes from './Routes';

import styles from './App.module.scss';

export default () => {
  const pathname = window.location.pathname;
  const [user, setUser] = useState<User | null>(null);
  const { data, isLoading, error, refetch } = useGetWorkspaceNameReports(pathname, {
    query: { refetchOnWindowFocus: false },
  });

  const { accessTokenPayload } = useOidcAccessToken();

  useEffect(() => {
    accessTokenPayload && setUser(getUserFromAccessToken(accessTokenPayload, 'pro'));
  }, [accessTokenPayload]);

  useEffect(() => {
    if (data?.data?.workspaces?.[0]?.expiration) {
      setTimeout(() => refetch(), timeToExpire(data.data.workspaces[0].expiration) + 1000);
    }
  }, [data?.data?.workspaces[0].expiration]);

  useEffect(() => {
    (async () => {
      if (user) {
        window.__preqinUser = user;

        if (user && isIntercomEnabled(pathname) && window.Intercom) {
          window.Intercom('boot', {
            api_base: 'https://api-iam.intercom.io',
            app_id: window._env_.INTERCOM_APP_ID,
            user_id: user?.id,
            name: user?.name.toLowerCase(),
            email: user?.email.toLowerCase(),
            created_at: Date.now(),
            company: user.preqinFirmName,
          });
        }
      }
    })();
  }, [user, pathname]);

  return (
    <Suspense
      fallback={
        <div className={styles.appLoader}>
          <AppLoader isLoading={isLoading} />
        </div>
      }
    >
      <BrowserRouter>
        <PowerBIProvider reportList={data} isLoadingReportList={isLoading} reportListError={error}>
          {/* API always returns single workspace */}
          <ApplicationLayout
            workspace={
              data?.data?.workspaces ? data?.data?.workspaces[0] : findWorkspaceByName(window.location.pathname)
            }
          >
            {isLoading && (
              <div id="loading" data-testid="main-loader" className={styles.loading}>
                <AppLoader isLoading={true} />
              </div>
            )}
            <Routes user={user} />
          </ApplicationLayout>
        </PowerBIProvider>
      </BrowserRouter>
    </Suspense>
  );
};
